// One practitioner, two licences. The sidebar header says which one you are looking at and
// lets you move. With a single track there is nothing to choose, so it renders nothing at all.
import { Link, useNavigate, useParams } from "react-router";
import { Check, ChevronDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTracks, type Track } from "@/features/dashboard/api";
import { Skeleton } from "@/components/ui/skeleton";

/** "LMFT · California" - the credential first, because that is what the person calls it. */
export function trackLabel(track: Track): string {
  return `${track.credentialName} · ${track.jurisdiction}`;
}

export function TrackSwitcher({ className }: { className?: string }) {
  const params = useParams();
  const navigate = useNavigate();
  const tracks = useTracks();

  if (tracks.isPending) return <Skeleton className="h-11 w-full rounded-[14px]" />;

  const items = tracks.data?.items ?? [];
  // One licence is not a choice. Two or more is.
  const first = items[0];
  if (items.length < 2 || !first) return null;

  const active = items.find((track) => track.id === params.trackId) ?? first;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={className}
          aria-label={`Licence: ${trackLabel(active)}. Switch licence`}
        >
          <span className="min-w-0 flex-1 truncate text-left">{trackLabel(active)}</span>
          <ChevronDown aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[var(--sidebar-width,15rem)]">
        {items.map((track) => {
          const current = track.id === active.id;
          return (
            <DropdownMenuItem
              key={track.id}
              onSelect={() => void navigate(`/t/${track.id}`)}
              aria-current={current ? "true" : undefined}
            >
              {current ? (
                <Check aria-hidden="true" />
              ) : (
                <span aria-hidden="true" className="size-[17px]" />
              )}
              <span className="truncate">{trackLabel(track)}</span>
              {current ? <span className="sr-only">Current licence</span> : null}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/onboarding">
            <Plus aria-hidden="true" />
            Add another licence
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
